import { motion } from 'framer-motion';
import type { MouseEventHandler, ReactNode } from 'react';

/**
 * Button — the single ceremonial button primitive. Owns the three
 * variants from DESIGN.md §4 and layers framer-motion on top for the
 * active scale feedback (CSS owns hover state via the `.btn-*` rules
 * in `index.css`, so we don't fight pseudo-classes).
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onClick?: MouseEventHandler<HTMLButtonElement>;
  title?: string;
  'aria-label'?: string;
  children?: ReactNode;
  className?: string;
}

export default function Button({
  variant = 'primary',
  size = 'md',
  icon,
  iconPosition = 'left',
  fullWidth = false,
  disabled = false,
  loading = false,
  type = 'button',
  onClick,
  title,
  'aria-label': ariaLabel,
  children,
  className,
}: ButtonProps) {
  const isDisabled = disabled || loading;

  const classes = [
    'btn-base',
    `btn-base--${size}`,
    `btn-${variant}`,
    fullWidth ? 'btn-fullwidth' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <motion.button
      type={type}
      className={classes}
      disabled={isDisabled}
      onClick={onClick}
      title={title}
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
      whileTap={!isDisabled ? { scale: 0.98 } : undefined}
      transition={{ duration: 0.1, ease: 'easeOut' }}
    >
      {icon && iconPosition === 'left' && (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>{icon}</span>
      )}
      {children && <span>{children}</span>}
      {icon && iconPosition === 'right' && (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>{icon}</span>
      )}
    </motion.button>
  );
}
